import React, { PropsWithChildren, useState, useEffect, useRef, useContext, useCallback } from 'react';
import { API_BASE_URL, API_ENDPOINTS, getHeaders, IS_DEBUG } from '../config/api';
import AppContext from './app-context';
import { PartnerLoginResponse, PartnerUser, Partner } from '../types/logistics';

// ===========================================
// JWT Utility Functions
// ===========================================

/**
 * Decodes a JWT payload without a library.
 * Returns null if the token is malformed.
 */
const decodeJwtPayload = (token: string): Record<string, any> | null => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(payload));
    } catch {
        return null;
    }
};

/**
 * Checks if a JWT token is expired (or will expire within bufferSeconds).
 * Returns true if expired or invalid, false if still valid.
 */
export const isTokenExpired = (token: string, bufferSeconds = 0): boolean => {
    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload.exp !== 'number') return true;
    const nowSeconds = Math.floor(Date.now() / 1000);
    return payload.exp - bufferSeconds <= nowSeconds;
};

/**
 * Returns seconds until token expiry, or 0 if expired/invalid.
 */
export const getTokenTimeRemaining = (token: string): number => {
    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload.exp !== 'number') return 0;
    const remaining = payload.exp - Math.floor(Date.now() / 1000);
    return Math.max(0, remaining);
};

/**
 * The backend code for "your password was right, your account is switched off".
 * Distinct from a plain 403 so we can end the session and explain, instead of
 * treating it as a per-action permission denial.
 */
export const ACCOUNT_DEACTIVATED_CODE = 'ACCOUNT_DEACTIVATED';

interface AuthContextType {
    user: PartnerUser | null;
    partner: Partner | null;
    isAuthenticated: boolean;
    dashboardEnabled: boolean;
    justLoggedIn: boolean;
    /**
     * Set when the backend refuses because the account is deactivated - either the
     * person individually, or the whole partner. Held rather than flashed as a toast:
     * it does not clear itself, nothing they can do on this screen will fix it, and
     * they need to be able to read who to contact. The login screen renders it.
     */
    deactivatedMessage: string | null;
    clearDeactivatedMessage: () => void;
    /**
     * Ends the session because the backend said the account is deactivated, keeping the
     * reason so the login screen can show it. A deactivation can land mid-shift, so this
     * is reachable from the API layer as well as from the sign-in and session-restore paths.
     */
    endSessionAsDeactivated: (message: string) => void;
    clearJustLoggedIn: () => void;
    updateUser: (user: PartnerUser | null) => void;
    updatePartner: (partner: Partner | null) => void;
    sendLogIn: (credentials: { email: string; password: string }) => void;
    logOut: () => void;
}

const AuthContext = React.createContext<AuthContextType>({
    user: null,
    partner: null,
    isAuthenticated: false,
    dashboardEnabled: false,
    justLoggedIn: false,
    deactivatedMessage: null,
    clearDeactivatedMessage: () => {},
    endSessionAsDeactivated: () => {},
    clearJustLoggedIn: () => {},
    updateUser: () => {},
    updatePartner: () => {},
    sendLogIn: () => {},
    logOut: () => {}
});

type authProps = {};

export const AuthContextProvider = (props: PropsWithChildren<authProps>) => {
    const appCtx = useContext(AppContext);
    const [user, setUser] = useState<PartnerUser | null>(null);
    const [partner, setPartner] = useState<Partner | null>(null);
    const [deactivatedMessage, setDeactivatedMessage] = useState<string | null>(null);
    const justLoggedInRef = useRef(false);

    const clearJustLoggedIn = useCallback(() => {
        justLoggedInRef.current = false;
    }, []);

    const updateUser = useCallback((userData: PartnerUser | null) => {
        setUser(userData);
    }, []);

    const updatePartner = useCallback((partnerData: Partner | null) => {
        setPartner(partnerData);
    }, []);

    const sendLogIn = useCallback(async (credentials: { email: string; password: string }) => {
        appCtx.newLoading('Logging in...');

        try {
            const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.LOGIN}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    email: credentials.email,
                    password: credentials.password
                })
            });

            const data: PartnerLoginResponse = await response.json();

            appCtx.newLoading('');

            if (response.ok) {
                setDeactivatedMessage(null);

                // Store user with JWT
                const userData: PartnerUser = {
                    ...data.user,
                    jwt: data.jwt
                };

                // Persist to localStorage
                localStorage.setItem('user', JSON.stringify(userData));
                localStorage.setItem('partner', JSON.stringify(data.partner));

                // Update state
                updateUser(userData);
                updatePartner(data.partner);
                justLoggedInRef.current = true;

                // Welcome message - use 'name' instead of 'firstName'
                appCtx.newMessage({
                    text: `Welcome, ${data.user.name}`,
                    type: 1
                });

                if (IS_DEBUG) {
                    console.log('[Auth] Login successful:', {
                        name: data.user.name,
                        email: data.user.email,
                        role: data.user.role,
                        partnerId: data.partner?.id
                    });
                }
            } else if (
                response.status === 403 &&
                (data as any).code === ACCOUNT_DEACTIVATED_CODE
            ) {
                // Not a credentials problem, and not something retrying fixes. The
                // message names who to contact, so it has to stay on screen.
                setDeactivatedMessage(
                    (data as any).error ||
                        'This account has been deactivated. Please contact Kit Keeper operations.'
                );
            } else {
                throw new Error((data as any).error || 'Login failed');
            }
        } catch (error: any) {
            console.error('[Auth] Login error:', error);
            appCtx.newLoading('');
            appCtx.newMessage({
                text: error.message || 'Login failed. Please check your credentials.',
                type: 0
            });
        }
    }, [appCtx, updateUser, updatePartner]);

    const clearDeactivatedMessage = useCallback(() => {
        setDeactivatedMessage(null);
    }, []);

    const endSessionAsDeactivated = useCallback((message: string) => {
        localStorage.removeItem('user');
        localStorage.removeItem('partner');
        setUser(null);
        setPartner(null);
        setDeactivatedMessage(message);
    }, []);

    const logOut = useCallback(() => {
        appCtx.newDialog({
            text: 'You are about to log out.',
            confirm: () => {
                localStorage.removeItem('user');
                localStorage.removeItem('partner');
                updateUser(null);
                updatePartner(null);
                appCtx.toggleMenu(false);
            }
        });
    }, [appCtx, updateUser, updatePartner]);

    // Restore session on mount and validate token
    useEffect(() => {
        const restoreSession = async () => {
            const storedUser = localStorage.getItem('user');
            const storedPartner = localStorage.getItem('partner');

            let userData: PartnerUser | null = null;
            let partnerData: Partner | null = null;

            if (storedUser) {
                try {
                    userData = JSON.parse(storedUser);
                } catch (e) {
                    console.error('[Auth] Failed to parse stored user:', e);
                    localStorage.removeItem('user');
                }
            }

            if (storedPartner) {
                try {
                    partnerData = JSON.parse(storedPartner);
                } catch (e) {
                    console.error('[Auth] Failed to parse stored partner:', e);
                    localStorage.removeItem('partner');
                }
            }

            // Validate token if we have a stored user with JWT
            if (userData?.jwt) {
                // Client-side expiry check first (no network needed)
                if (isTokenExpired(userData.jwt)) {
                    if (IS_DEBUG) {
                        console.log('[Auth] Stored token is expired, clearing session');
                    }
                    localStorage.removeItem('user');
                    localStorage.removeItem('partner');
                    userData = null;
                    partnerData = null;
                } else if (navigator.onLine) {
                    // Token looks valid client-side — verify with backend
                    try {
                        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ME}`, {
                            headers: getHeaders(userData.jwt)
                        });
                        if (response.ok) {
                            // Parse response to refresh user + partner data (includes logisticsDashboardEnabled)
                            try {
                                const meData = await response.json();
                                if (meData.user && userData) {
                                    userData = { ...userData, ...meData.user, jwt: userData.jwt };
                                    localStorage.setItem('user', JSON.stringify(userData));
                                }
                                if (meData.partner) {
                                    partnerData = meData.partner;
                                    localStorage.setItem('partner', JSON.stringify(partnerData));
                                }
                            } catch { /* ignore parse errors */ }
                        } else {
                            if (IS_DEBUG) {
                                console.log('[Auth] Token validation failed:', response.status);
                            }
                            // A token outlives a deactivation, so this is where someone
                            // who was signed in when it happened finds out. Without the
                            // message they are just returned to the login screen with no
                            // idea why, and their password still "works".
                            if (response.status === 403) {
                                try {
                                    const body = await response.json();
                                    if (body?.code === ACCOUNT_DEACTIVATED_CODE && body?.error) {
                                        setDeactivatedMessage(body.error);
                                    }
                                } catch { /* ignore parse errors — still sign them out */ }
                            }
                            localStorage.removeItem('user');
                            localStorage.removeItem('partner');
                            userData = null;
                            partnerData = null;
                        }
                    } catch (e) {
                        // Network error during validation — keep the session
                        // (they might be offline momentarily)
                        if (IS_DEBUG) {
                            console.log('[Auth] Token validation skipped (network error)');
                        }
                    }
                }
            }

            setUser(userData);
            setPartner(partnerData);
            appCtx.finishInit();
        };

        restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Proactive expiry warning — check every 5 minutes
    const expiryWarningShown = useRef(false);
    useEffect(() => {
        if (!user?.jwt) {
            expiryWarningShown.current = false;
            return;
        }

        const EXPIRY_WARNING_SECONDS = 3600; // 1 hour
        const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

        const checkExpiry = () => {
            if (!user?.jwt || expiryWarningShown.current) return;

            const remaining = getTokenTimeRemaining(user.jwt);
            if (remaining === 0) {
                // Already expired — clear session
                localStorage.removeItem('user');
                localStorage.removeItem('partner');
                setUser(null);
                setPartner(null);
                appCtx.newMessage({
                    text: 'Your session has expired. Please login again.',
                    type: 0
                });
            } else if (remaining <= EXPIRY_WARNING_SECONDS) {
                expiryWarningShown.current = true;
                const minutes = Math.round(remaining / 60);
                appCtx.newMessage({
                    text: `Your session expires in ~${minutes} minutes. Please save your work and re-login soon.`,
                    type: 0
                });
            }
        };

        // Check immediately, then on interval
        checkExpiry();
        const intervalId = setInterval(checkExpiry, CHECK_INTERVAL_MS);
        return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.jwt]);

    const providerConfig: AuthContextType = {
        user,
        partner,
        isAuthenticated: !!user?.jwt,
        dashboardEnabled: user?.logisticsDashboardEnabled ?? partner?.logisticsDashboardEnabled ?? false,
        justLoggedIn: justLoggedInRef.current,
        deactivatedMessage,
        clearDeactivatedMessage,
        endSessionAsDeactivated,
        clearJustLoggedIn,
        updateUser,
        updatePartner,
        sendLogIn,
        logOut
    };

    return (
        <AuthContext.Provider value={providerConfig}>
            {props.children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
